import { NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
	afterNextRender,
	Component,
	computed,
	ElementRef,
	HostListener,
	inject,
	Injector,
	OnInit,
	output,
	signal,
	viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { faHeart, faSignOutAlt, faUser, faUserShield, faUsers } from '@fortawesome/free-solid-svg-icons';
import { PortfolioNavDropdownComponent } from '@infodocs/shared/components/header/portfolio-nav-dropdown/portfolio-nav-dropdown.component';
import { CompanyService } from '@infodocs/company/company.service';
import { AuthService } from '@infodocs/shared/services/auth.service';
import {
	ManualAddCompanyComponent
} from '@infodocs/portfolio/modals/add-company-modal/manual-add-company/manual-add-company.component';
import { CompanyPreviewComponent } from '@infodocs/portfolio/modals/company-preview/company-preview.component';
import SearchesForCompaniesComponent from '@infodocs/portfolio/modals/searches-for-companies.component';
import { ArchivedBadgeComponent } from '@infodocs/shared/components/archived-badge/archived-badge.component';
import { SearchButtonComponent } from '@infodocs/shared/components/buttons/search-button/search-button.component';
import { Alert } from '@infodocs/shared/interfaces/alert';
import { Company } from '@infodocs/shared/interfaces/company-portfolio';
import { CompanySearchResult } from '@infodocs/shared/interfaces/company-search-result';
import {
	UpgradeSubscriptionModalComponent
} from '@infodocs/shared/modals/upgrade-subscription-modal/upgrade-subscription-modal.component';
import { IconComponent } from '@infodocs/shared/components/icon/icon.component';
import { PrivateHeaderSearchService } from '@infodocs/shared/services/private-header-search.service';
import { VerificationGuardService } from '@infodocs/shared/services/verification-guard.service';
import { SharedService } from '@infodocs/shared/shared.service';
import { getCompanySearchErrorMessage } from '@infodocs/shared/utils/company-search-errors';
import { resolveManualAddCompanyPrefill } from '@infodocs/shared/utils/manual-add-company-prefill';
import { marked } from 'marked';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { distinctUntilChanged, finalize, map, takeUntil } from 'rxjs';

const VERIFICATION_SHIELD_MIN_LEVEL = 2;

interface HeaderSearchRestoreState {
	query: string;
	companiesSearched: CompanySearchResult[];
	currentPortfolioCompanies: Company[];
	errorMessage: string | undefined;
	resultsScrollTop: number;
	windowScrollY: number;
}

@Component({
	selector: 'app-private-header',
	templateUrl: './private-header.component.html',
	styleUrls: ['./private-header.component.scss'],
	imports: [
		RouterLink,
		IconComponent,
		NgClass,
		ArchivedBadgeComponent,
		SearchButtonComponent,
		ReactiveFormsModule,
		AlertComponent,
		PortfolioNavDropdownComponent,
	]
})
export class PrivateHeaderComponent extends SearchesForCompaniesComponent implements OnInit {
	public readonly searchQuery = output<string>();
	protected readonly alerts = signal<Alert[]>([]);
	protected readonly environment = environment;
	protected readonly faUsersIcon = faUsers;
	protected readonly faHeartIcon = faHeart;
	protected readonly faSignOutAltIcon = faSignOutAlt;
	protected readonly profileNavIcon = computed(() => {
		const level = this.verificationGuard.verificationLevel() ?? 0;

		return level >= VERIFICATION_SHIELD_MIN_LEVEL ? faUserShield : faUser;
	});
	protected readonly isSearchingCompany = signal(false);
	protected readonly isSearchResultsOpen = signal(false);
	protected readonly companiesSearched = signal<CompanySearchResult[]>([]);
	protected readonly errorMessage = signal<string | undefined>(undefined);
	protected readonly auth = inject(AuthService);
	protected readonly sharedService = inject(SharedService);
	protected readonly searchQueryControl = new FormControl('');
	protected readonly companyService = inject(CompanyService);
	protected readonly headerSearchService = inject(PrivateHeaderSearchService);
	protected readonly companiesData = toSignal(this.companyService.watchPortfolioCompanies());
	protected readonly portfolioLandingNavigation = computed(() => this.accountService.getPortfolioLandingNavigation());
	protected readonly currentPortfolioCompanies = signal<Company[]>([]);
	private readonly verificationGuard = inject(VerificationGuardService);
	private readonly http = inject(HttpClient);
	private readonly injector = inject(Injector);
	private readonly searchResultsDropdown = viewChild<ElementRef<HTMLElement>>('searchResultsDropdown');
	private searchRestoreState?: HeaderSearchRestoreState;
	private ignoreNextDocumentClick = false;

	public ngOnInit() {
		this.getAlert();
		this.syncSearchControlFromService();

		this.searchQueryControl.valueChanges
			.pipe(
				map(value => (value ?? '').trim()),
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe(query => this.onSearchQueryChanged(query));

		this.headerSearchService
			.onSearchTermChange()
			.pipe(takeUntil(this.destroy$))
			.subscribe(term => this.syncSearchControlFromService(term));
	}

	@HostListener('document:click', ['$event'])
	protected onDocumentClick(event: MouseEvent): void {
		if (this.ignoreNextDocumentClick) {
			this.ignoreNextDocumentClick = false;
			return;
		}

		const target = event.target;

		if (!(target instanceof Element)) {
			return;
		}

		if (target.closest('#table-portfolio_filter')) {
			return;
		}

		this.closeSearchResults();
	}

	protected closeSearchResults(): void {
		this.isSearchResultsOpen.set(false);
	}

	protected searchForCompanies(): void {
		const rawQuery = this.searchQueryControl.value ?? '';
		const trimmedQuery = rawQuery.trim();

		this.syncSearchTerm(trimmedQuery);

		if (!trimmedQuery) {
			this.resetSearchResults();

			return;
		}

		this.isSearchingCompany.set(true);

		if (this.isOnPortfolioPage()) {
			this.currentPortfolioCompanies.set([]);
		} else {
			this.currentPortfolioCompanies.set(
				this.companiesData()?.data?.filter((company: Company) => {
					return [company.company_name, company.registration_number]
						.filter(Boolean)
						.some(property => {
							return property.toString().toLowerCase().includes(trimmedQuery.toLowerCase());
						});
				}).slice(0, 3) ?? []
			);
		}

		this.accountService
			.searchCompany(trimmedQuery.toUpperCase().replace(/\//g, '.'))
			.pipe(
				finalize(() => {
					this.isSearchingCompany.set(false);
					this.isSearchResultsOpen.set(true);
				})
			)
			.subscribe({
				next: companies => {
					const portfolioCompaniesRegNumbers = this.currentPortfolioCompanies().map(company => company.registration_number);

					const filteredCompanies = companies.filter(
						company => !portfolioCompaniesRegNumbers.includes(company.CompanyInformation.CompanyRegistrationNumber)
					);
					this.companiesSearched.set(filteredCompanies);
				},
				error: error => this.errorMessage.set(getCompanySearchErrorMessage(error, trimmedQuery))
			});
	}

	protected override onBack(): void {
		this.ignoreNextDocumentClick = true;
		this.applyRestoredSearchState();
	}

	protected override previewCompany(company: CompanySearchResult) {
		this.captureSearchRestoreState();
		this.closeSearchResults();

		return this.modalService.show(CompanyPreviewComponent, {
			initialState: {
				registrationNumber: company.CompanyInformation.CompanyRegistrationNumber,
				companyExists: company.CompanyExists ?? company.CompanyInformation.CompanyExists,
				existingCompanyId: company.CompanyInformation.CompanyId,
				companySearchResult: company,
				search_option: this.getSearchOption(),
				onBack: () => this.onBack(),
				onFlowComplete: () => this.onFlowComplete(),
			}
		});
	}

	protected openManualAddCompanyModal() {
		this.captureSearchRestoreState();
		this.closeSearchResults();

		this.modalService.show(ManualAddCompanyComponent, {
			class: 'modal-dialog',
			ignoreBackdropClick: true,
			initialState: {
				...resolveManualAddCompanyPrefill(this.searchQueryControl.value, this.companiesSearched()),
				onBack: () => this.onBack()
			}
		});
	}

	protected openUpgradeSubscriptionModal() {
		this.modalService.show(UpgradeSubscriptionModalComponent, {
			ignoreBackdropClick: true,
			initialState: {
				flow: 'navbar',
				initialUpgrade: true
			}
		}).onHide?.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.accountService.fetchAccount());
	}

	protected markAlertAsRead(alertId: number) {
		return this.http.get<Alert>(`/api/alert/${alertId}`).pipe(takeUntil(this.destroy$))
			.subscribe(result => this.getAlert());
	}

	protected setAlertType(alertType: string): string {
		switch (alertType) {
			case 'info':
				return 'alert alert-info mb-0 alert-dismissible fade show';
			case 'incident':
				return 'alert alert-warning mb-0 alert-dismissible fade show';
			case 'outage':
				return 'alert alert-danger mb-0 alert-dismissible fade show';
			case 'feature':
			case 'subscription_feature':
				return 'alert alert-success mb-0 alert-dismissible fade show';
			default:
				return 'alert alert-info mb-0 alert-dismissible fade show';
		}
	}

	protected markdownToHtml(markdown: string): Promise<string> | string {
		const renderer = this.configureMarked();

		marked.setOptions({
			renderer,
			gfm: true,
			breaks: true
		});

		return marked(markdown);
	}

	private getAlert(): void {
		this.http.get<Alert>('/api/alert')
			.pipe(takeUntil(this.destroy$))
			.subscribe((data: any) => this.alerts.set([data]));
	}

	private onSearchQueryChanged(query: string): void {
		this.closeSearchResults();
		this.companiesSearched.set([]);
		this.errorMessage.set(undefined);
		this.syncSearchTerm(query);

		if (!query) {
			this.resetSearchResults(false);
		}
	}

	private isOnPortfolioPage(): boolean {
		return window.location.pathname === '/portfolio/company'
			|| window.location.pathname === '/portfolio/companies'
			|| window.location.pathname === '/portfolio/trust'
			|| window.location.pathname === '/portfolio/individuals';
	}

	private resetSearchResults(shouldShowResults: boolean = true): void {
		this.currentPortfolioCompanies.set(this.companiesData()?.data ?? []);
		this.companiesSearched.set([]);
		this.errorMessage.set(undefined);
		this.isSearchingCompany.set(false);
		this.isSearchResultsOpen.set(shouldShowResults);
	}

	private captureSearchRestoreState(): void {
		this.searchRestoreState = {
			query: (this.searchQueryControl.value ?? '').trim(),
			companiesSearched: [...this.companiesSearched()],
			currentPortfolioCompanies: [...this.currentPortfolioCompanies()],
			errorMessage: this.errorMessage(),
			resultsScrollTop: this.searchResultsDropdown()?.nativeElement.scrollTop ?? 0,
			windowScrollY: window.scrollY,
		};
	}

	private applyRestoredSearchState(): void {
		const state = this.searchRestoreState;
		if (!state) {
			this.isSearchResultsOpen.set(true);
			return;
		}

		this.searchQueryControl.setValue(state.query, { emitEvent: false });
		this.syncSearchTerm(state.query);
		this.companiesSearched.set(state.companiesSearched);
		this.currentPortfolioCompanies.set(state.currentPortfolioCompanies);
		this.errorMessage.set(state.errorMessage);
		this.isSearchResultsOpen.set(true);
		window.scrollTo(0, state.windowScrollY);
		this.restoreResultsScroll(state.resultsScrollTop);
	}

	private restoreResultsScroll(scrollTop: number): void {
		afterNextRender(() => {
			const dropdown = this.searchResultsDropdown()?.nativeElement;
			if (dropdown) {
				dropdown.scrollTop = scrollTop;
			}
		}, { injector: this.injector });
	}

	private syncSearchTerm(term: string): void {
		this.searchQuery.emit(term);
		this.headerSearchService.setSearchTerm(term);
	}

	private syncSearchControlFromService(term: string = this.headerSearchService.getSearchTerm()): void {
		const currentValue = (this.searchQueryControl.value ?? '').trim();
		if (currentValue === term) {
			return;
		}

		this.searchQueryControl.setValue(term, { emitEvent: false });
	}

	public submitSearch(event?: Event): void {
		event?.preventDefault();
		this.searchForCompanies();
	}

	private configureMarked() {
		const renderer = new marked.Renderer();

		renderer.link = ({ href, title, text }) => {
			return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
		};

		return renderer;
	}
}
